import { useCallback, useEffect, useState } from 'react';
import {
  IClientSettings,
  IOrganizationalUnit,
  getAllOrganizationalUnitSettings,
  updateOrganizationalUnitSettings,
  getAllOrganizationalUnits,
  IOrganizationalIds,
  getTeamsCount,
  getAccountManagerCount,
} from 'wisestep-api';

import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { OrganizationalUnitDefaultPayload } from '../helper/ClientSettingsHelper';
import { OrganizationalScopeType } from '../types';

import AddOrganizationalUnitDialog from './AddOrganizationalUnitDialog';
import OrganizationalUnitLayout from './OrganizationalUnitLayoutNew';

interface IOrganizationalUnitSettings {
  title?: string;
  scopeType?: OrganizationalScopeType;
  readonlyMode?: boolean;
}

export interface countsMap {
  [id: string]: string;
}

const OrganizationalUnitSettings = ({
  title,
  scopeType,
  readonlyMode = false,
}: IOrganizationalUnitSettings) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [organizationalUnits, setOrganizationalUnits] = useState<IOrganizationalUnit[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [loader, setLoader] = useState<boolean>(false);
  const [organizationalUnit, setOrganizationalUnit] = useState<IOrganizationalUnit>(
    OrganizationalUnitDefaultPayload
  );
  const [teamCount, setTeamCount] = useState<countsMap>({});
  const [accountManagerCount, setAccountManagerCount] = useState<countsMap>({});
  const [onEdit, setOnEdit] = useState(-1);

  const fetchData = useCallback(async () => {
    setLoader(true);
    const response = scopeType
      ? await getAllOrganizationalUnits(scopeType)
      : await getAllOrganizationalUnitSettings();
    const uniqueUnits = response.data.filter(
      (unit, index, self) => index === self.findIndex((item) => item.id === unit.id)
    );
    setOrganizationalUnits(uniqueUnits);
    setLoader(false);
    if (readonlyMode && scopeType === OrganizationalScopeType.Teams) {
      const orgIds = uniqueUnits.map((res) => res.id ?? '');
      const payload: IOrganizationalIds = {
        organizationalIds: orgIds,
      };
      const count = await getTeamsCount(payload);
      setTeamCount(count.data.countsByOrganizationalId);
    } else if (readonlyMode && scopeType === OrganizationalScopeType.Account_Managers) {
      const orgIds = uniqueUnits.map((res) => res.id ?? '');
      const payload: IOrganizationalIds = {
        organizationalIds: orgIds,
      };
      const count = await getAccountManagerCount(payload);
      setAccountManagerCount(count.data.countsByOrganizationalId);
    }
  }, [readonlyMode, scopeType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onUpdate = async (data: IOrganizationalUnit) => {
    const organizationUnitData: IOrganizationalUnit = { ...organizationalUnit };
    organizationUnitData.description = data.description;
    organizationUnitData.name = data.name;
    if (data.id !== undefined) {
      organizationUnitData.id = data.id;
      await updateOrganizationalUnitSettings(organizationUnitData);
    }
    setNotification(success('Organizational Unit has been updated successfully !!'));
    setOpenDialog(false);
    fetchData();
  };

  const getOrganizationalUnit = (index: number) => {
    const tempPayload = organizationalUnits[index];
    setOrganizationalUnit(tempPayload);
    setOpenDialog(true);
    setOnEdit(1);
  };

  return (
    <>
      <OrganizationalUnitLayout
        title={title ?? 'Organizational Units'}
        backUrl={
          title === 'Teams Organizational Units'
            ? '/workspace-users-and-teams'
            : '/app-settings/teams-and-users'
        }
        headerList={['Unit Name', 'Visibility']}
        organizationalUnits={organizationalUnits}
        addButtonText="Organizational Unit"
        handleAddButton={() => {
          setOpenDialog(true);
          setOrganizationalUnit(OrganizationalUnitDefaultPayload);
          setOnEdit(0);
        }}
        loader={loader}
        getOrganizationalUnit={getOrganizationalUnit}
        readonlyMode={readonlyMode}
        teamCount={teamCount}
        accountManagerCount={accountManagerCount}
        scopeType={scopeType}
      />
      {openDialog && (
        <AddOrganizationalUnitDialog
          ouOpenDialog={openDialog}
          oncloseDialog={() => setOpenDialog(false)}
          organizationalUnitValue={organizationalUnit}
          setOrganizationalUnitsState={setOrganizationalUnits}
          dataCallback={(data: IClientSettings) => onUpdate(data)}
          onEdit={onEdit}
          scopeType={scopeType}
        />
      )}
    </>
  );
};

export default OrganizationalUnitSettings;
